import { ToastId } from '@chakra-ui/react';
import { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  invalidateExtractedTablesQuery,
  useSubmitTableForExtraction,
} from '@frontend/api';
import {
  type ExtractedTableTaskMetaType,
  PartWorkerTasks,
  TaskStatus,
  TaskResolution,
} from '@frontend/api';
import { useToast } from '@frontend/components';

import { getActiveFile } from 'modules/common/viewer/data/reducer';
import {
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  type SuggestionAnnotationCommand,
} from 'modules/common/viewer/pdfViewer/annotationCommands';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';
import { getSuggestionAnnotationsCommandQueue } from 'modules/common/viewer/pdfViewer/data/reducer';

import { useTaskStatusTracking } from 'utils/hooks/taskTracking/useTaskStatusTracking';

/**
 * Handles the business logic for the manual cropped table extraction flow, including adding
 * annotations to the pdf viewer, showing toasts to indicate progress, and refreshing the data
 * for the page upon successful extraction.
 */
export const useCroppedTableInteractions = () => {
  const activeFileUuid: string | null =
    useSelector(getActiveFile)?.uuid ?? null;
  const cleanupCallbacksRef = useRef<Record<string, () => void>>({});

  const { mutate: submitTableForExtraction } = useSubmitTableForExtraction({
    queryOptions: {
      onSuccess: (_, variables) => {
        if (activeFileUuid && variables.cleanUpTempAnnotation) {
          cleanupCallbacksRef.current[activeFileUuid] =
            variables.cleanUpTempAnnotation;
        }
      },
      onError: () => {
        console.error('Error submitting table for extraction');
      },
    },
  });

  // Handle the submission of table extraction requests
  const dispatch = useDispatch();
  const suggestionAnnotationsCommandQueue: SuggestionAnnotationCommand[] =
    useSelector(getSuggestionAnnotationsCommandQueue);
  const refreshCommands = suggestionAnnotationsCommandQueue.filter(
    (cmd) =>
      cmd.type === SUGGESTION_ANNOTATION_COMMAND_TYPES.REFRESH_EXTRACTED_TABLES,
  );
  useEffect(() => {
    if (refreshCommands.length > 0 && activeFileUuid) {
      refreshCommands.forEach((cmd) => {
        if (cmd.payload) {
          submitTableForExtraction({
            ...cmd.payload,
            fileUuid: activeFileUuid,
          });
        }
      });
      // Clean up the processed commands
      dispatch(
        suggestionAnnotations.acknowledgeCommands({
          commandIds: refreshCommands.map((cmd) => cmd.id),
        }),
      );
    }
  }, [
    suggestionAnnotationsCommandQueue,
    dispatch,
    submitTableForExtraction,
    refreshCommands,
    activeFileUuid,
  ]);

  const toast = useToast();
  const toastIdsRef = useRef<Record<string, ToastId>>({});

  // Handle callbacks for cropped table task status updates
  const { registerCallback, unregisterCallback } = useTaskStatusTracking();

  useEffect(() => {
    if (!activeFileUuid) return;

    const callbackKey = `cropped_table_analysis_${activeFileUuid}`;

    const callbackFunctions = {
      onStart: () => {
        const toastId = toast({
          title: 'Scanning for tables...',
          status: 'info',
          duration: null,
        });
        toastIdsRef.current[activeFileUuid] = toastId;
      },
      onSuccess: (messageMeta: ExtractedTableTaskMetaType) => {
        const hasFoundTables = messageMeta.totalTableCount > 0;

        if (toastIdsRef.current[activeFileUuid]) {
          toast.update(toastIdsRef.current[activeFileUuid], {
            title: hasFoundTables
              ? `Added ${
                  messageMeta.totalTableCount === 1
                    ? 'table'
                    : `${messageMeta.totalTableCount} tables`
                } to Found in Files`
              : 'No tables found',
            status: hasFoundTables ? 'success' : 'warning',
            duration: 10000,
          });
        }

        // Trigger refetch of extracted tables if we have found tables
        if (hasFoundTables) {
          invalidateExtractedTablesQuery({ partFileUuid: activeFileUuid });
        }

        // Clean up manual selection annotation
        if (cleanupCallbacksRef.current[activeFileUuid]) {
          cleanupCallbacksRef.current[activeFileUuid]();
          delete cleanupCallbacksRef.current[activeFileUuid];
        }

        // Wipe toast ID from dictionary
        delete toastIdsRef.current[activeFileUuid];
      },
      onError: () => {
        if (toastIdsRef.current[activeFileUuid]) {
          toast.update(toastIdsRef.current[activeFileUuid], {
            title: 'Failed to extract table',
            status: 'error',
          });
        }

        // Clean up manual selection annotation
        if (cleanupCallbacksRef.current[activeFileUuid]) {
          cleanupCallbacksRef.current[activeFileUuid]();
          delete cleanupCallbacksRef.current[activeFileUuid];
        }
        delete toastIdsRef.current[activeFileUuid];
      },
    };

    registerCallback(callbackKey, ({ updateMessages }) => {
      for (const update of updateMessages) {
        if (
          update.task === PartWorkerTasks.CROPPED_TABLE_ANALYSIS &&
          update.entityKey === activeFileUuid
        ) {
          switch (update.status) {
            case TaskStatus.QUEUED:
              callbackFunctions.onStart();
              break;
            case TaskStatus.COMPLETED:
              if (update.resolution === TaskResolution.SUCCESS) {
                callbackFunctions.onSuccess(
                  update.meta as ExtractedTableTaskMetaType,
                );
              } else {
                callbackFunctions.onError();
              }
              break;
          }
          break;
        }
      }
    });

    return () => unregisterCallback(callbackKey);
  }, [activeFileUuid, registerCallback, toast, unregisterCallback]);
};
